import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {GridItemHTMLElement, GridStack, GridStackNode, GridStackWidget} from 'gridstack';
import {WidgetDto} from '../../../../../Models/Widgets/widget.interface';
import {WidgetSwitchComponent} from '../widget-switch/widget-switch.component';
import {HapticsImpactManagerService} from '../../../../../Services/Platform/Capacitor/haptics-impact-manager.service';

@Component({
  selector: 'app-widgets-grid',
  templateUrl: './widgets-grid.component.html',
  styleUrls: ['./widgets-grid.component.scss'],
  standalone: false
})
export class WidgetsGridComponent implements AfterViewInit, OnChanges, OnDestroy {

  @ViewChild('gridEl', {static: false}) gridEl!: ElementRef<HTMLElement>;
  @ViewChildren(WidgetSwitchComponent) widgetSwitches!: QueryList<WidgetSwitchComponent>;

  @Input() widgets: WidgetDto[] = [];
  @Input() isStoreView: boolean = false;
  @Input() editable: boolean = false;
  @Input() showEmptyMessage: boolean = false;

  @Output() setFavoritesWidgets: EventEmitter<void> = new EventEmitter<void>();
  @Output() actionWidget: EventEmitter<WidgetDto> = new EventEmitter<WidgetDto>();
  @Output() orderChanged: EventEmitter<WidgetDto[]> = new EventEmitter<WidgetDto[]>();

  private grid?: GridStack;
  private viewReady: boolean = false;
  private destroyed: boolean = false;
  private suppressChange: boolean = false;
  private resizeObserver?: ResizeObserver;

  private static readonly ROW_STEP = 1000;
  private readonly tabletViewport: boolean = typeof window !== 'undefined' && window.matchMedia('(min-width: 768px)').matches;

  constructor(
    private zone: NgZone,
    private hapticsImpactManager: HapticsImpactManagerService,
  ) {
  }


  ngAfterViewInit() {
    this.viewReady = true;

    this.reconcile();
    this.widgetSwitches.changes.subscribe(() => this.reconcile());
  }


  ngOnChanges(changes: SimpleChanges) {

    if (!this.viewReady) {
      return;
    }

    if (changes['editable'] && this.grid) {
      this.grid.setStatic(!this.editable);
    }
  }


  ngOnDestroy() {
    this.destroyed = true;
    this.destroyGrid();
  }


  /**
   * Crear la instancia de GridStack una sola vez
   *
   */
  private initGrid(): boolean {

    if (this.grid) {
      return true;
    }

    if (!this.gridEl?.nativeElement?.isConnected) {
      return false;
    }

    this.grid = GridStack.init(
      {
        column: 2,
        margin: 6,
        disableResize: true,
        float: false,
        staticGrid: !this.editable,
        cellHeight: 10,
        sizeToContent: true,
      },
      this.gridEl.nativeElement
    );

    this.grid.on('change', (_event: Event, nodes: GridStackNode[]) => this.onGridChange(nodes));
    this.grid.on('dragstart', () => this.hapticsImpactManager.hapticsImpactLight());

    this.observeContentResize();
    return true;
  }


  /**
   * Reconciliar el grid con el array de widgets
   *
   */
  private reconcile() {

    this.zone.runOutsideAngular(() => {

      if (this.grid && !this.grid.el?.isConnected) {
        this.destroyGrid();
      }

      if (!this.initGrid() || !this.grid) {
        return;
      }

      const layout = this.layoutFromOrder();

      this.runSuppressingChange(() => this.grid!.load(layout));
      this.refreshContentObserver();
    });
  }


  /**
   * Derivar el ancho (columnas) del widget a partir de `fullWidth`
   *
   */
  widgetWidth(widget: WidgetDto): number {
    return widget.fullWidth && !this.tabletViewport ? 2 : 1;
  }


  /**
   * Actualizar los datos de los widgets
   *
   */
  refreshWidgetsData(loadedData: boolean = true) {
    this.widgetSwitches.forEach((widgetSwitch) => widgetSwitch.refreshWidgetsData(loadedData));
  }


  /**
   * Calcular las posiciones (x/y/w) derivadas del ORDEN del array y de la COLUMNA persistida (`col`)
   *
   */
  private positionsFromOrder(): Map<string, GridStackWidget> {
    const positions = new Map<string, GridStackWidget>();

    let row = 0;
    let leftTaken = false;
    let rightTaken = false;

    const y = () => row * WidgetsGridComponent.ROW_STEP;
    const openNewRow = () => {
      row++;
      leftTaken = false;
      rightTaken = false;
    };

    this.widgets.forEach((widget) => {
      const width = this.widgetWidth(widget);

      if (width === 2) {

        if (leftTaken || rightTaken) {
          openNewRow();
        }

        positions.set(widget.id, {x: 0, y: y(), w: 2});
        openNewRow();
        return;
      }

      const desired = widget.col ?? (leftTaken ? 1 : 0);

      if (desired === 0) {

        if (leftTaken) {
          openNewRow();
        }

        positions.set(widget.id, {x: 0, y: y(), w: 1});
        leftTaken = true;

      } else {

        if (rightTaken) {
          openNewRow();
        }

        positions.set(widget.id, {x: 1, y: y(), w: 1});
        rightTaken = true;
        openNewRow();
      }
    });

    return positions;
  }


  /**
   * Construir el layout
   *
   */
  private layoutFromOrder(): GridStackNode[] {
    const positions = this.positionsFromOrder();
    const root = this.gridEl?.nativeElement;

    return this.widgets.reduce<GridStackNode[]>((layout, widget) => {
      const position = positions.get(widget.id);
      const el = root?.querySelector(`[gs-id="${widget.id}"]`) as GridItemHTMLElement | null;

      if (position && el) {
        layout.push({...position, id: widget.id, el});
      }

      return layout;
    }, []);
  }


  /**
   * Destruir el grid sin eliminar el DOM
   *
   */
  private destroyGrid() {
    this.resizeObserver?.disconnect();
    this.resizeObserver = undefined;

    this.grid?.destroy(false);
    this.grid = undefined;
  }


  /**
   * Reordenado hecho por el usuario
   *
   */
  private onGridChange(_nodes: GridStackNode[]) {

    if (this.suppressChange || !this.editable) {
      return;
    }

    const newOrder = this.orderFromGrid();

    if (!newOrder) {
      return;
    }

    this.zone.run(() => this.orderChanged.emit(newOrder));
  }


  /**
   * Construir el nuevo orden del array a partir de la disposición visual del grid
   *
   */
  private orderFromGrid(): WidgetDto[] | null {

    if (!this.grid) {
      return null;
    }

    const nodes = this.grid.save(false) as GridStackNode[];

    if (nodes.length !== this.widgets.length) {
      return null;
    }

    const byId = new Map(this.widgets.map((widget) => [widget.id, widget]));

    const ordered = nodes
      .slice()
      .sort((a, b) => (a.y ?? 0) - (b.y ?? 0) || (a.x ?? 0) - (b.x ?? 0))
      .map((node) => {
        const widget = byId.get(node.id as string);

        if (!widget) {
          return undefined;
        }

        return this.withColumn(widget, node);
      });

    if (ordered.some((widget) => !widget)) {
      return null;
    }

    return ordered as WidgetDto[];
  }


  /**
   * Devolver una copia del widget con su columna (`col`) según el nodo del grid
   *
   */
  private withColumn(widget: WidgetDto, node: GridStackNode): WidgetDto {

    if (this.widgetWidth(widget) === 2) {
      const {col, ...rest} = widget;
      return rest;
    }

    return {...widget, col: (node.x ?? 0) === 1 ? 1 : 0};
  }


  /**
   * Crear el observer que reajusta la altura de cada item según su contenido
   *
   */
  private observeContentResize() {
    const grid = this.grid;

    if (!grid) {
      return;
    }

    this.resizeObserver = new ResizeObserver((entries) => {
      this.runSuppressingChange(() => {
        for (const entry of entries) {
          const item = (entry.target as HTMLElement).closest('.grid-stack-item') as HTMLElement | null;

          if (item) {
            grid.resizeToContent(item);
          }
        }
      });
    });
  }


  /**
   * Re-suscribir el ResizeObserver al contenido de los items actuales tras reconciliar
   *
   */
  private refreshContentObserver() {

    if (!this.grid || !this.resizeObserver) {
      return;
    }

    this.resizeObserver.disconnect();
    this.grid.getGridItems().forEach((el) => this.observeItemContent(el));
  }


  /**
   * Suscribir el contenido interno de un item al observer de redimensionado
   *
   */
  private observeItemContent(el: HTMLElement) {
    const inner = el.querySelector('.grid-stack-item-content > *');

    if (inner) {
      this.resizeObserver?.observe(inner);
    }
  }


  /**
   * Ejecutar una mutación programática del grid sin que dispare el reordenado/persistencia
   *
   */
  private runSuppressingChange(fn: () => void) {
    this.suppressChange = true;

    try {
      fn();
    } finally {
      this.suppressChange = false;
    }
  }

}
